import {
    defineComponent,
    h,
    type DefineComponent,
    type FunctionalComponent,
    type PropType,
} from 'vue';
import { useDivisible } from './composables';

/**
 * A renderless component helper for divisible parts.
 */
export const Divisible = defineComponent({
    // eslint-disable-next-line vue/multi-word-component-names
    name: 'Divisible',
    props: {
        target: {
            type: String,
            required: true,
        },
        is: {
            type: [String, Object, Function, null] as PropType<
                string | DefineComponent | FunctionalComponent | null
            >,
            default: null,
        },
        observedOnly: {
            type: Boolean,
            default: false,
        },
    },
    setup: (props, { slots, attrs }) => {
        const { give } = useDivisible();
        const { part, observed } = give.slot(props.target, {
            slotKey: 'default',
        });

        return () => {
            // Render nothing when observed or observedOnly.
            if (observed.value || props.observedOnly) return null;

            // Render the unobserved slot. (this slot is never rendered for the receiver).
            if (slots.unobserved) return slots.unobserved({ part });

            // Use the `is` property if it is set.
            if (props.is) {
                if (typeof props.is === 'string')
                    return h(
                        props.is,
                        attrs,
                        slots.default ? slots.default() : undefined
                    );
                return h(props.is, attrs, { ...slots });
            }

            // Render the default slot otherwise.
            return slots.default ? slots.default() : undefined;
        };
    },
});
